/**
 * sl-r9ix.3.3: Trial Card for Billing Tab
 *
 * Shows trial status with remaining days, progress bar, expiry date,
 * and upgrade CTA. Displayed instead of TeamCard when subscription
 * status is Trialing.
 */
import { Button } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { WorkspaceSubscriptionService } from '@affine/core/modules/cloud';
import { SubscriptionStatus } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import type { SettingState } from '../../types';
import { CardNameLabelRow } from './card-name-label-row';
import * as styles from './styles.css';

const TRIAL_DURATION_DAYS = 14;

export const TrialCard = ({
  onChangeSettingState,
}: {
  onChangeSettingState?: (settingState: SettingState) => void;
}) => {
  const t = useI18n();
  const workspaceSubscriptionService = useService(WorkspaceSubscriptionService);
  const subscription = useLiveData(
    workspaceSubscriptionService.subscription.subscription$
  );

  const trialEnd = subscription?.trialEnd;
  const trialStart = subscription?.trialStart;

  const locale = t.language?.startsWith('en') ? 'en-US' : 'de-DE';

  const { daysLeft, progressPercent, expiryDateStr } = useMemo(() => {
    if (!trialEnd) {
      return { daysLeft: 0, progressPercent: 100, expiryDateStr: '' };
    }

    const now = Date.now();
    const endMs = new Date(trialEnd).getTime();
    const startMs = trialStart
      ? new Date(trialStart).getTime()
      : endMs - TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000;

    const totalMs = endMs - startMs;
    const elapsedMs = now - startMs;
    const remainingMs = Math.max(0, endMs - now);

    const days = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
    const percent =
      totalMs > 0
        ? Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100))
        : 100;
    const dateStr = new Date(trialEnd).toLocaleDateString(locale);

    return { daysLeft: days, progressPercent: percent, expiryDateStr: dateStr };
  }, [trialEnd, trialStart, locale]);

  const handleUpgrade = useCallback(() => {
    onChangeSettingState?.({
      activeTab: 'workspace:plans',
      scrollAnchor: 'cloudPricingPlan',
    });
  }, [onChangeSettingState]);

  return (
    <div className={styles.trialCard}>
      <div className={styles.trialCardHeader}>
        <div className={styles.trialCardInfo}>
          <CardNameLabelRow
            cardName={t[
              'com.streamlain.payment.billing-setting.plan-card-title'
            ]({
              plan: subscription?.plan ?? 'Pro',
            })}
            status={SubscriptionStatus.Trialing}
          />
          <div className={styles.trialCardDaysLeft}>
            {t['com.streamlain.payment.billing-setting.trial.days-left']({
              days: String(daysLeft),
            })}
          </div>
          <div className={styles.trialCardExpiry}>
            {t['com.streamlain.payment.billing-setting.trial.expires-on']({
              date: expiryDateStr,
            })}
          </div>
        </div>
      </div>

      <div className={styles.trialProgressBarTrack}>
        <div
          className={styles.trialProgressBarFill}
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <SettingRow
        name={t['com.streamlain.payment.billing-setting.trial.upgrade-hint']()}
        desc=""
      >
        <Button variant="primary" onClick={handleUpgrade}>
          {t['com.streamlain.payment.billing-setting.trial.upgrade-button']()}
        </Button>
      </SettingRow>
    </div>
  );
};
